import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Receipt, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg, fmtUSD, fmtDate } from "@/lib/api";
import { useBillingMeta } from "@/components/admin/BillingPanel";

const empty = { amount: "", method: "Bank transfer", reference: "", note: "", notify: true };

export function PaymentsPanel({ shipment, onSaved }) {
  const api = useApi();
  const { data: meta } = useBillingMeta();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const payments = shipment.billing?.payments || [];
  const balance = shipment.billing_summary?.balance || 0;

  const record = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("post", `/admin/shipments/${shipment.id}/payments`, { ...f, amount: Number(f.amount) });
      toast.success(`Payment recorded${r.email === "sent" ? ". Receipt emailed." : ""}`);
      setF(empty);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const voidPay = async (p) => {
    const reason = window.prompt(`Why are you voiding ${p.receipt_number} (${fmtUSD(p.amount)})?`);
    if (!reason) return;
    try { await api("post", `/admin/shipments/${shipment.id}/payments/${p.id}/void`, { reason }); toast.success("Payment voided"); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="payments-panel">
      <h3 className="font-display font-semibold text-[#0A192F]">Payments</h3>
      <form onSubmit={record} className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Amount (USD)</Label>
          <div className="flex gap-2"><Input required type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount-input" />
            {balance > 0 && <Button type="button" variant="outline" onClick={() => setF({ ...f, amount: String(balance) })} data-testid="payment-fill-balance-btn">Full {fmtUSD(balance)}</Button>}</div></div>
        <div className="space-y-1.5"><Label>Method</Label>
          <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
            <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(meta?.payment_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label>Reference (optional)</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="payment-reference-input" /></div>
        <div className="space-y-1.5"><Label>Note (optional)</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="payment-note-input" /></div>
        <div className="flex items-center gap-3 sm:col-span-2"><Switch checked={f.notify} onCheckedChange={(v) => setF({ ...f, notify: v })} data-testid="payment-notify-switch" /><span className="text-sm">Email a receipt to the customer</span></div>
        <Button type="submit" disabled={busy} className="sm:col-span-2 bg-emerald-600 hover:bg-emerald-700" data-testid="payment-record-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Record payment</Button>
      </form>
      <div className="divide-y divide-slate-100" data-testid="payments-list">
        {!payments.length && <p className="text-sm text-slate-500 py-2">No payments recorded yet.</p>}
        {payments.map((p) => (
          <div key={p.id} className={`py-3 flex flex-wrap items-center justify-between gap-2 ${p.voided ? "opacity-50" : ""}`} data-testid={`payment-row-${p.receipt_number}`}>
            <div>
              <p className="font-mono font-semibold">{fmtUSD(p.amount)} <span className="font-sans text-sm text-slate-500">· {p.method}{p.reference ? ` · ref ${p.reference}` : ""}</span></p>
              <p className="text-xs text-slate-400">{p.receipt_number} · {fmtDate(p.paid_at)} · by {p.recorded_by}{p.voided ? ` · VOIDED: ${p.void_reason}` : ""}</p>
            </div>
            {!p.voided && <div className="flex gap-2">
              <Link to={`/admin/invoice/${shipment.id}?receipt=${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border border-slate-200 rounded-sm hover:border-[#0A192F]" data-testid={`payment-receipt-${p.receipt_number}`}><Receipt className="h-3.5 w-3.5" /> Receipt</Link>
              <button onClick={() => voidPay(p)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border border-slate-200 rounded-sm hover:border-red-400 text-red-600" data-testid={`payment-void-${p.receipt_number}`}><Ban className="h-3.5 w-3.5" /> Void</button>
            </div>}
          </div>
        ))}
      </div>
    </div>
  );
}
